'use client'

import { useMemo } from 'react'
import { useGoogleApiLoader } from '@hooks/use-google-api-loader'
import { isScmgGeoData, parseGeocodeResult, type GeoData } from '@lib/utilities/location'

type Coordinates = {
  lat: number
  lng: number
}

export const coordinatesCache = new Map<string, GeoData>()
export const placeIdCache = new Map<string, GeoData>()
export const addressCache = new Map<string, GeoData>()

const geocodeByCoordinates = async (geocoder: google.maps.Geocoder, coordinates: Coordinates) => {
  const cacheKey = `${coordinates.lat},${coordinates.lng}`

  if (coordinatesCache.has(cacheKey)) {
    return coordinatesCache.get(cacheKey) || null
  }

  const { results } = await geocoder.geocode({ location: coordinates })
  if (results.length) {
    const result = parseGeocodeResult(results[0])
    coordinatesCache.set(cacheKey, result)
    return result
  }

  return null
}

const geocodeById = async (geocoder: google.maps.Geocoder, placeId: string) => {
  if (placeIdCache.has(placeId)) {
    return placeIdCache.get(placeId) || null
  }

  const { results } = await geocoder.geocode({ placeId })
  if (results.length) {
    const result = parseGeocodeResult(results[0])
    if (!isScmgGeoData(result)) {
      return null
    }

    placeIdCache.set(placeId, result)
    return result
  }

  return null
}

const geocodeByAddress = async (geocoder: google.maps.Geocoder, address: string) => {
  if (addressCache.has(address)) {
    return addressCache.get(address) || null
  }

  const { results } = await geocoder.geocode({ address })
  if (results.length) {
    const result = parseGeocodeResult(results[0])
    if (!isScmgGeoData(result)) {
      return null
    }

    addressCache.set(address, result)
    return result
  }

  return null
}

export function useGeocoder() {
  const { isLoaded, loadError } = useGoogleApiLoader()

  return useMemo(() => {
    if (isLoaded) {
      const geocoder = new google.maps.Geocoder()
      return {
        geocoder,
        geocodeByCoordinates: (coordinates: Coordinates) => geocodeByCoordinates(geocoder, coordinates),
        geocodeById: (placeId: string) => geocodeById(geocoder, placeId),
        geocodeByAddress: (address: string) => geocodeByAddress(geocoder, address),
        isLoaded,
        loadError,
      }
    }

    return {
      geocoder: null,
      geocodeByCoordinates: null,
      geocodeById: null,
      geocodeByAddress: null,
      isLoaded,
      loadError,
    }
  }, [isLoaded, loadError])
}